import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { generatePath, useParams } from 'react-router';
import * as RestApi from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import { ILinkLookup } from 'sections/design/template-management/entities/link-config';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { rcsConfigToCompilerTemplateValue } from '../utils/rcsConfigToCardContent';
import { getCompilationContextFromSnapshotJson } from '../utils/getCompilationContextFromSnapshotJson';
import { checkCompiledRcsPreviewLimits } from '../utils/checkCompiledRcsPreviewLimits';
import { MAX_CARD_TITLE_LENGTH, MAX_DESCRIPTION_LENGTH } from '../utils/validateRcsConfig';
import { rcsPreviewTemplate } from '../utils/queries';

/**
 * RCS template editor: runs compile preview via API, updates preview state, and shows a
 * persistent warning toast when compiled title/description exceed configured max lengths.
 * Dismisses that warning when a later preview succeeds within limits.
 */
export function useRcsPreview() {
  const { t } = useTranslation('designs');
  const { showNotification, closeNotification } = useNotifications();
  const dispatch = useRcsTemplateEditorDispatch();
  const { rcsConfig, snapshotJson, templateType } = useRcsTemplateEditorState();
  const requestIdRef = useRef(0);

  const { siteId, language } = useParams<{ siteId: string; id: string; language: string }>();

  const { data: linkLookups = [] } = useQuery(
    ['linklookup', siteId],
    async () => {
      const result = await RestApi.get<ILinkLookup[]>(
        generatePath(TemplateApiEndpoints.GET_LINK_LOOKUPS, { siteId: siteId! }),
      );
      return result.data ?? [];
    },
    { enabled: Boolean(siteId), staleTime: 5 * 60 * 1000 },
  );

  const { mutate: preview, isLoading } = useMutation(rcsPreviewTemplate);

  const previewTemplate = useCallback(() => {
    if (!siteId || !language) return;

    const templateValue = rcsConfigToCompilerTemplateValue(rcsConfig);
    const compilationContext = getCompilationContextFromSnapshotJson(snapshotJson);
    const requestId = ++requestIdRef.current;

    const onSuccess = (data: { compiled: string; precompiled: string }) => {
      if (requestId !== requestIdRef.current) return;
      dispatch({
        type: 'UPDATE_PREVIEW_INITIAL',
        payload: {
          compiledTemplate: data.compiled,
          lastCompiledRcsPayload: templateValue,
          lastCompiledCompilationContext: compilationContext,
        },
      });

      const limits = checkCompiledRcsPreviewLimits(data.compiled);
      const overLimit = limits && (limits.titleOverLimit || limits.descriptionOverLimit);
      if (overLimit) {
        let message: string;
        if (limits.titleOverLimit && limits.descriptionOverLimit) {
          message = t('rcsTemplateEditor.preview.compiledPreviewBothWarning', {
            titleLimit: MAX_CARD_TITLE_LENGTH,
            descriptionLimit: MAX_DESCRIPTION_LENGTH,
            titleCount: limits.titleLength,
            descriptionCount: limits.descriptionLength,
          });
        } else if (limits.titleOverLimit) {
          message = t('rcsTemplateEditor.preview.compiledPreviewTitleWarning', {
            titleLimit: MAX_CARD_TITLE_LENGTH,
            count: limits.titleLength,
          });
        } else {
          message = t('rcsTemplateEditor.preview.compiledPreviewDescriptionWarning', {
            descriptionLimit: MAX_DESCRIPTION_LENGTH,
            count: limits.descriptionLength,
          });
        }
        showNotification(message, 'warning', 0);
      } else {
        closeNotification();
      }
    };

    const onError = (error: Error) => {
      if (requestId !== requestIdRef.current) return;
      showNotification(error.message, 'error');
    };

    preview(
      {
        templateValue,
        compilationContext,
        siteId,
        language,
        linkConfig: linkLookups,
        templateType,
        t,
      },
      { onSuccess, onError },
    );
  }, [
    closeNotification,
    dispatch,
    language,
    linkLookups,
    preview,
    rcsConfig,
    siteId,
    showNotification,
    snapshotJson,
    t,
    templateType,
  ]);

  return { previewTemplate, isLoading };
}
